import { DragDropProvider } from "@dnd-kit/react";
import { useSortable as useDndSortable, type UseSortableInput } from "@dnd-kit/react/sortable";
import {
  Accessibility,
  Feedback,
  KeyboardSensor,
  PointerActivationConstraints,
  PointerSensor,
} from "@dnd-kit/dom";
import { useSyncExternalStore, type ComponentProps } from "react";

export {
  DragOverlay,
  useDraggable,
  useDroppable,
  useDragOperation,
  type DragEndEvent,
  type UseDroppableInput,
} from "@dnd-kit/react";
export { isSortable } from "@dnd-kit/react/sortable";
export { move as moveItems } from "@dnd-kit/helpers";

const motionQuery = "(prefers-reduced-motion: reduce)";
function subscribeMotion(listener: () => void) {
  const media = window.matchMedia(motionQuery);
  media.addEventListener("change", listener);
  return () => media.removeEventListener("change", listener);
}
function useReducedMotion() {
  return useSyncExternalStore(
    subscribeMotion,
    () => window.matchMedia(motionQuery).matches,
    () => false,
  );
}

export function useSortable(input: UseSortableInput): ReturnType<typeof useDndSortable> {
  const reducedMotion = useReducedMotion();
  return useDndSortable({ ...input, transition: reducedMotion ? null : { duration: 160 } });
}

/** Opt-in keyboard navigation between explicit destinations, without sortable DOM mutations. */
class TargetKeyboardSensor extends KeyboardSensor {
  protected override handleMove(direction: "left" | "right" | "up" | "down", event: KeyboardEvent) {
    const { source, position, target } = this.manager.dragOperation;
    if (source?.data["keyboardNavigation"] !== "targets") return super.handleMove(direction, event);
    const origin = position.current;
    const horizontal = direction === "left" || direction === "right";
    const sign = direction === "left" || direction === "up" ? -1 : 1;
    const candidates = [...this.manager.registry.droppables].flatMap((drop) => {
      if (
        drop.disabled ||
        !drop.accepts(source) ||
        drop.id === target?.id ||
        drop.data["keyboardDisabled"] ||
        (drop.data["keyboardAxis"] &&
          drop.data["keyboardAxis"] !== (horizontal ? "horizontal" : "vertical")) ||
        !drop.shape
      )
        return [];
      const center = drop.shape.center;
      const along = horizontal ? center.x - origin.x : center.y - origin.y;
      const across = horizontal ? center.y - origin.y : center.x - origin.x;
      if (along * sign <= 1) return [];
      return [{ center, score: Math.abs(along) + Math.abs(across) * 4 }];
    });
    candidates.sort((a, b) => a.score - b.score);
    const next = candidates[0];
    event.preventDefault();
    if (next) this.manager.actions.move({ event, to: next.center });
  }
}

// A little travel prevents clicks from becoming drags. Touch scrolling stays
// available everywhere except the explicit handle, which uses touch-action:none.
const sensors = [
  PointerSensor.configure({
    activationConstraints: [new PointerActivationConstraints.Distance({ value: 6 })],
  }),
  TargetKeyboardSensor,
];
const accessibility: NonNullable<ConstructorParameters<typeof Accessibility>[1]> = {
  announcements: {
    dragstart: ({ operation: { source } }) =>
      source ? `Picked up ${source.data["label"] ?? "item"}.` : undefined,
    dragover: ({ operation: { source, target } }) => {
      if (!source || source.id === target?.id) return;
      return target
        ? `Over ${target.data["label"] ?? "drop area"}.`
        : "Outside a drop area. Escape cancels the move.";
    },
    dragend: ({ operation: { source, target }, canceled }) => {
      if (!source) return;
      return canceled || !target ? "Move canceled." : `Dropped ${source.data["label"] ?? "item"}.`;
    },
  },
};

export function DragDropRoot({ onDragEnd, ...props }: ComponentProps<typeof DragDropProvider>) {
  const reducedMotion = useReducedMotion();
  return (
    <DragDropProvider
      {...props}
      sensors={sensors}
      onDragEnd={(event, manager) => {
        // A drop outside this context is a cancellation, including any temporary
        // DOM ordering managed by the sortable plugin.
        if (!event.operation.target) event.canceled = true;
        return onDragEnd?.(event, manager);
      }}
      plugins={(defaults) => [
        ...defaults,
        { plugin: Accessibility, options: accessibility },
        Feedback.configure({
          dropAnimation: reducedMotion ? null : { duration: 160, easing: "ease-out" },
        }),
      ]}
    />
  );
}

/** Cross-list moves remount handles. Restore keyboard focus after React commits. */
export function restoreDragFocus(root: HTMLElement | null, id: string) {
  requestAnimationFrame(() => {
    const handle = [...(root?.querySelectorAll<HTMLElement>("[data-drag-handle]") ?? [])].find(
      (element) => element.dataset["dragHandle"] === id,
    );
    (handle ?? root)?.focus({ preventScroll: true });
  });
}
